import { MapPin, Phone, Mail } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Reveal } from "../components/Reveal";
import { PHONE_DISPLAY, EMAIL, MAPS_LINK, waLink } from "../lib/spa";
import { WhatsAppIcon } from "../components/WhatsAppIcon";
import { Seo } from "../components/Seo";

export default function Contact() {
  const { t } = useLanguage();

  const cards = [
    { icon: MapPin, title: t("contact.location"), value: t("contact.locationValue"), note: t("contact.locationNote"), href: MAPS_LINK, external: true },
    { icon: Phone, title: t("contact.phone"), value: PHONE_DISPLAY, note: t("contact.phoneHours"), href: waLink(t("booking.generalMessage")), external: true },
    { icon: Mail, title: t("contact.email"), value: EMAIL, note: null, href: `mailto:${EMAIL}`, external: false },
  ];

  return (
    <div data-testid="contact-page" className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8 sm:pt-40">
      <Seo title={t("seo.contact.title")} description={t("seo.contact.desc")} />
      <Reveal>
        <p className="text-[10px] uppercase tracking-[0.45em] text-gold">{t("contact.label")}</p>
        <h1 className="mt-3 font-display text-4xl font-light text-foreground sm:text-5xl lg:text-6xl">{t("contact.title")}</h1>
        <p className="mt-4 max-w-xl text-sm leading-relaxed text-foreground/72">{t("contact.subtitle")}</p>
      </Reveal>

      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {cards.map((c, i) => (
          <Reveal key={c.title} delay={i * 0.1}>
            <a
              href={c.href}
              {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className="group block h-full rounded-2xl border border-foreground/8 bg-card p-8 transition-all duration-300 hover:-translate-y-1 hover:border-gold/30 hover:shadow-[0_20px_60px_-15px_rgba(212,175,55,0.2)]"
            >
              <c.icon className="h-6 w-6 text-gold" strokeWidth={1.5} />
              <h3 className="mt-4 text-xs uppercase tracking-[0.3em] text-foreground/65">{c.title}</h3>
              <p className="mt-3 font-display text-2xl font-light text-foreground group-hover:text-gold-light">{c.value}</p>
              {c.note && <p className="mt-2 text-xs text-foreground/50">{c.note}</p>}
            </a>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2}>
        <div className="mt-12 overflow-hidden rounded-2xl border border-gold/20 bg-gradient-to-br from-gold/10 via-transparent to-transparent p-10 text-center sm:p-16">
          <h2 className="font-display text-3xl font-light text-foreground sm:text-4xl">{t("contact.readyTitle")}</h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-foreground/72">{t("contact.readyDesc")}</p>
          <a
            data-testid="contact-whatsapp-cta"
            href={waLink(t("booking.generalMessage"))}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-3 rounded-full bg-gold px-10 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-all duration-300 hover:bg-gold-light hover:shadow-[0_0_30px_rgba(212,175,55,0.3)]"
          >
            <WhatsAppIcon className="h-5 w-5" />
            {t("contact.whatsappCta")}
          </a>
          <div className="mt-7 flex items-center justify-center gap-3 text-xs text-foreground/55">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
            </span>
            {t("contact.availability")} · {t("contact.fewSlots")}
          </div>
        </div>
      </Reveal>
    </div>
  );
}
